
import React, { useState, useEffect } from 'react';
import { WeatherQuote } from '@/types/weather';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

interface CustomQuoteInputProps {
  onCustomQuoteChange: (quote: WeatherQuote | null) => void;
}

const formSchema = z.object({
  quoteText: z.string().max(200, 'Text cannot exceed 200 characters'),
  author: z.string().max(50, 'Name cannot exceed 50 characters').optional(),
});

type FormValues = z.infer<typeof formSchema>;

const CustomQuoteInput: React.FC<CustomQuoteInputProps> = ({ onCustomQuoteChange }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      quoteText: '',
      author: '',
    }
  });

  const handleFormChange = (values: FormValues) => {
    if (values.quoteText) {
      onCustomQuoteChange({
        en: values.quoteText,
        it: values.quoteText,
        author: values.author,
        isCustom: true
      });
    } else {
      onCustomQuoteChange(null);
    }
  };

  // Watch form changes to update quote in real-time
  useEffect(() => {
    const subscription = form.watch((value) => {
      handleFormChange(value as FormValues);
    });
    return () => subscription.unsubscribe();
  }, [form, onCustomQuoteChange]);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Custom quote</h3>
      <Form {...form}>
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="quoteText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Text</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Enter your quote (maximum 200 characters)" 
                    className="resize-none" 
                    {...field} 
                  />
                </FormControl>
                <FormDescription className="text-xs text-right">
                  {field.value.length}/200
                </FormDescription>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="author"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Author</FormLabel>
                <FormControl>
                  <Input placeholder="Author name (optional)" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </Form>
    </div>
  );
};

export default CustomQuoteInput;
